import {IDocumentInWspHouse} from "back/wsp/widgets/wed/schemaMetaWsp";
import {EventsMgr, IEvents, IEventsMgr} from "lib/commons/events";
import {EHttpStatusCode, IO} from "lib/commons/io/io";
import {DOM} from "lib/commons/xml/dom";
import {IJmlSet, JML} from "lib/commons/xml/jml";
import {EWsState, isWsMsgError, IWsExecFrame} from "lib/core/universe";
import {JUser} from "lib/core/user";
import {IDocHolderSync} from "lib/edit/docHolder";
import {CommentsState, IXmlTypedHouseEvents, OXmlTypedHouseConfig, XmlTypedHouse} from "lib/edit/ot/xmlTypedHouse";
import {ITEM} from "lib/wsp/item";
import {JSrcFields, JSrcIdent, SRC, srcId, srcRef, srcUri} from "lib/wsp/src";
import "lib/wsp/wsp_Perms";
import {ItemType} from "lib/wsp/wspMetaUi";
import {ERROR} from "lib/core/errorReport";
import {JMoanServerMsg, JWspStChangeMsg, JWspUriChangeMsg, JWspWorkingInitStates, JWspWorkingState, WSP, Wsp, wspCd, wspId, wspRef, WspSrv, wspUri} from "lib/wsp/wsp";
import {PlaceDocHolder, RoadExecFrameWs, WspXmlHouse, WspXmlSlaveHouse} from "lib/wsp/wspHouse";
import {ICmEditors} from "lib/wsp/wspsLiveCm";
import {House, InitSlaveRep} from "lib/edit/ot/urban";
import {AskForSaveReq} from "lib/edit/ot/houseOt";
import {ESrcRights} from "lib/commons/security";

/** "Etat de travail" sur chaque noeud par chaque "Place" . */
export const enum EWspWorkingSt {
	r = 'r', //reading, noeud ouvert en consultation, notifié ainsi aux autres users.
	w = 'w', //writing, noeud en édition
	l = 'l', //en écoute des changements des states sur les autres device, mais pas vu des autres devices.
	none = '', //suppression de toute écoute.
}

/** Evènements relatifs à un noeud. */
export const enum EWspChangesEvts {
	u = 'u', //update, noeud modifié
	r = 'r', //remove, noeud supprimé
	s = 's', //status, statut du noeud modifié (erreurs lié à des modifications des items liés)
	perm = 'perm', //Changement de permissions sur le noeud
	drvState = 'drvState', //chgt d'état d'un item d'un calque de dérivation
	drfState = 'drfState', //chgt d'état d'un item d'un calque brouillon
	lcSt = 'lcSt', //chgt du cycle de vie sur ce noeud
	rspUsrs = 'rspUsrs' //chgt des responsables sur ce noeud
}

/** Events diffusés au niveau de la house. */
export interface IWspXmlHouseEvents extends IXmlTypedHouseEvents {

	/** Evènements relatifs au processus d'enregistrement persistant. */
	onSave: (state: 'saving' | 'saved' | 'saveFailed', e?: any) => void | Promise<void>;

	/** Evènements relatifs vérrouillage / déverrouillage de l'éditeur modifiant l'état readOnly des éditeurs. */
	onLock: (locked: boolean) => void;

	/** Détection d'un changement de permission sur cette house. */
	onPermChange: (house: IWspXmlHouse) => void;
}

/** */
export interface IWspXmlHouse extends XmlTypedHouse {

	/** events étendus de la house. */
	listeners: EventsMgr<IWspXmlHouseEvents>

	/** L'id de la house est le wspRef. */
	id: wspRef

	/** wspUri du contenu édité. */
	wspUri: wspUri

	/** wspId du contenu édité. */
	wspId?: wspId

	/** this.wspId || this.wspUri */
	wspRef: wspRef;

	/** Si la house correspond aux métas d'une res, wspUri de l'item. */
	wspUriItem: wspUri

	/** Wsp du contenu édité. */
	wsp: Wsp

	/** itemType de l'item issu du WspMetaUi du Wsp.  */
	itemType: ItemType

	/**
	 * Sous-ensemble Fields de l'item concerné permettant :
	 * - de trouver l'ItemType et son schema associé,
	 * - de détecter des déplacements / renommages sans tuer la house (si en db avec srcId)
	 * - de détecter des modifs concurrentes (en cas de rupture de connexion temporaire)
	 */
	srcFields: JWspXmlHouseFields

	/** Autre device verrouillant actuellement cette house. */
	lockedBy?: ICollabLock

	/** Cache des états des autres devices pour cette house. */
	states?: JWspWorkingState[]

	/** Date depuis laquelle plus aucun docHolder n'est associé. Pour envisager le cleanup. */
	notUsedSince: number

	/** Si cette house a été remplacée par une autre suite à un reload. */
	killed: boolean

	/** timer encours pour l'enregistreemnt auto. */
	saveTimer?: number
}

/**
 * Propriétés nécessaires pour la gestion de la house.
 * srcSt : pour édition des metas, détecter que les metas sont en corbeille.
 */
interface JWspXmlHouseFields extends Pick<JSrcFields, "srcUri" | "itModel" | "srcDt" | "srcSt" | "srcId" | "srcStamp" | "srcRi" | "srcRoles" | "srcLiveUri"> {}

export interface ICollabLock {
	clId: string
	account: string
}

type JInitSlaveRepDatas = {
	body: IJmlSet
	srcFields: JWspXmlHouseFields
	states?: Jsonisable
}

const WspLiveDEBUG = false;

/**
 * Service de gestion des modifications et activités sur les ateliers exploitation WebSocket
 * et les svc serveurs wspWorking et wspChanges.
 *
 * Note : l'API doit rester compatible avec une impl qui proxierait vers un SharedWorker
 * (pas de retour synchrones des méthodes de WspLive et WspLivePlace ou retour undefined autorisé).
 */
export class WspsLive {

	/**
	 * Le serveur et les services ws wspWorking et wspChanges sont-ils disponibles ?
	 * Si true, l'appel à WspsLive.sendMsg() peut alors être utilisée, les msgs seront placés en attente si le processus d'init interne
	 * n'est pas encore entièrement achevé ou si une interruption réseau est en cours.
	 */
	isAvailable: boolean | undefined;

	/**
	 * Nombre de houses référencées à partir duquel on commence à purger les plus anciennes.
	 */
	cleanupHousesThreshold = 20;

	/**
	 * Nombre de houses référencées à partir duquel les houses doivent être purgées immédiatement.
	 * Une erreur est émise si on ne parvient pas à descendre en dessous de ce seuil.
	 */
	cleanupHousesNowThreshold = 200;

	readonly _wsFrame: IWsExecFrame;

	/**
	 * Ateliers enregistrés auprès du service.
	 * Note : Set et non Map car plusieurs objets Wsp avec le même wspCd pourraient
	 * être enregistrés.
	 */
	_wsps = new Set<Wsp>();

	/** Places enregistrées auprès du service. */
	_places = new Set<WspsLivePlace>();

	/** Houses ouvertes: la house est doublement référencée dans la map par son wspUri ET son wspId ET son wspUriItem si c'est une res. */
	protected _houses = new Map<wspUri | wspId, IWspXmlHouse>();

	/** Houses en cours de chargement indexées selon son wspRef d'appel. */
	protected _fetchingHouses = new Map<wspRef, Promise<IWspXmlHouse>>();

	/** Pour remoteOtHouse. */
	protected _initingSlaveHouses = new Map<wspRef, IWspXmlHouse>();

	/** Set des houses dédoublonnées par rapport à _houses*/
	protected _allHouses = new Set<IWspXmlHouse>();

	/** Listeners en attente que la connexion soit active. */
	protected _pendingReady: ((ready: boolean) => void)[];

	/** Editeurs Cm en cours (isolés pour chargement si utilisé). */
	cmEditors: ICmEditors;

	/**
	 * Pool des events à dispatcher, tuple de [wspUri, event].
	 * Objectif: batch via un wsp.startSrcFieldsBatch(), wsp.fetchSrcFields(), wsp.endSrcFieldsBatch(),
	 * en une seule requete au serveur, suite à plusieurs events et aux demandes des différentes Places.
	 */
	//TODO protected _poolChangesEvts: string[];

	constructor(public readonly wspServer: WspSrv) {
		this._wsFrame = wspServer.chain.wsFrames.ws;
		if (this._wsFrame) {
			this._wsFrame.lcListeners.on('opened', this.onWsOpened.bind(this));
			this._wsFrame.lcListeners.on('closed', this.onWsClosed.bind(this));
			this._wsFrame.msgListeners.on('wspWorking', this.onWspWorkingMsg.bind(this));
			this._wsFrame.msgListeners.on('wspChanges', this.onWspChangesMsg.bind(this));
			this._wsFrame.msgListeners.on('moan', this.onMoanMsg.bind(this));
			const saveAll = this.saveAllHouses.bind(this);
			if (wspServer.chain.config.local) window.addEventListener('blur', saveAll);
			window.addEventListener('beforeunload', saveAll);
			if (this._wsFrame.wsState === EWsState.wsOpened) this.onWsOpened(); //déjà ouvert, on initialise
		} else {
			//ws non dispo.
			this.isAvailable = false;
		}
		wspServer.chain.auth.listeners.on('loggedUserChanged', (oldUser: JUser, newUser: JUser) => {
			if (newUser) {
				for (const wsp of this._wsps) if (wsp.isInError) wsp.forceReload();
			} else {
				//Déconnexion, on cleanup tout.
				this._wsps.clear();
				for (const house of this._allHouses.values()) house.killed = true; //on bloque d'eventuels traitements async
				this._houses.clear();
				this._allHouses.clear();
			}
		}, -1);

	}

	/**
	 * Promesse pour attendre que le service soit disponible (true) ou en état définitivement indisponible (false).
	 * Promesse jamais rejetée.
	 */
	waitForReady(): Promise<boolean> {
		if (this._connectionReady) return Promise.resolve(true);
		if (this.isAvailable === false) return Promise.resolve(false);
		this.startIfNeeded();
		return new Promise<boolean>((resolve) => {
			if (this._wsFrame.wsState === EWsState.wsOpened) resolve(this.isAvailable);
			else {
				if (!this._pendingReady) this._pendingReady = [];
				this._pendingReady.push(resolve);
			}
		});
	}

	/**
	 * Création d'une place enregistrée auprès de ce service.
	 * Permet à un composant graphique de s'abonner aux évènements qui l'intéresse et d'interroger le service.
	 * Ne pas oublier de gérer la fermeture de cette place via WspsLivePlace.closePlace().
	 * Les listeners de la place sont appelés dans l'ordre de déclaration des places (api Set).
	 * Les listeners d'un container instanciant une place seront donc toujours appelés avant les places
	 * instanciées dans les views filles (ex: ItemView et ses itemMain views).
	 */
	newPlace(): WspsLivePlace {
		this.startIfNeeded();
		return new WspsLivePlace(this);
	}

	/**
	 * Cherche un Wsp déjà référencé ou on en crée un nouveau si createIfNone.
	 * Si ce Wsp a vocation a être utilisé sur une longue période, il est nécessaire
	 * d'appeler Wsp.listenChanges() pour être certain de bénéificier des mises à jour
	 * du Wsp issues du server. Ne pas oublier d'appeler Wsp.stopListenChanges() à la fin.
	 */
	findWsp(wspCd: wspCd, createIfNone?: boolean): Wsp {
		for (const wsp of this._wsps) if (wsp.code === wspCd || wsp.wspAlias === wspCd) return wsp;
		return createIfNone ? new Wsp(this.wspServer, wspCd) : null;
	}

	/** Fournit les houses uniques pour les places.
	 * @param srcRef Attention, pas nécessairement un srcId en db : cas des corbeille, histo, etc.
	 */
	async _getHouse(wsp: Wsp, srcRef: srcUri | srcId): Promise<IWspXmlHouse | null> {
		const wspRef: wspRef = WSP.buildWspRef(wsp.code, srcRef);
		let house = this._houses.get(wspRef);
		if (house) return house;
		let fetching = this._fetchingHouses.get(wspRef);
		if (fetching) return fetching;
		if (this.wspServer.config.remoteHouseOt) {
			if (!wsp.isFullLoaded) await wsp.forceAllItemsLoaded(document.documentElement); //forceAllItemsLoaded QUE pour builder => remoteHouseOt en enum 'moan' ou 'collab' ?
			fetching = this.__connectRemoteHouse(wsp, srcRef, wspRef);
		} else {
			fetching = this.__fetchHouse(wsp, srcRef, wspRef);
		}
		this._fetchingHouses.set(wspRef, fetching);
		try {
			house = await fetching;
		} finally {
			this._fetchingHouses.delete(wspRef);
		}
		return house;
	}

	/** NE PAS UTILISER. cf _getHouse() */
	private async __connectRemoteHouse(wsp: Wsp, srcUriOrId: srcUri | srcId, wspUriOrId: wspUri | wspId): Promise<IWspXmlHouse | null> {
		if (!WspXmlSlaveHouseCstr || !RoadExecFrameWsCstr) {
			//import("lib/edit/ot/moan.js") Pour forcer l'enregistrement de MoanState
			const m = (await Promise.all([import("lib/wsp/wspHouse.js"), import("lib/edit/ot/moan.js")]))[0];
			WspXmlSlaveHouseCstr = m.WspXmlSlaveHouse;
			RoadExecFrameWsCstr = m.RoadExecFrameWs;
		}
		const house = new WspXmlSlaveHouseCstr({
			id: wspUriOrId,
			historyMinEntries: 300
		}) as IWspXmlHouse;
		const roadEnd = new RoadExecFrameWsCstr(this._wsFrame, house);
		roadEnd.door = house.openDoor(roadEnd, {roadToMaster: true});
		this._initingSlaveHouses.set(wspUriOrId, house);
		const h = await house.initFromMaster(async (reply: InitSlaveRep) => {
			this._initingSlaveHouses.delete(wspUriOrId);
			const srcFields = (reply.datas as JInitSlaveRepDatas).srcFields;
			const wspRefFound = WSP.buildWspRef(wsp.code, srcFields);
			if (SRC.isSrcId(srcUriOrId) && srcUriOrId !== srcFields.srcId) {
				//Risque de créer 2 houses en doublon car le wspRef initial était un alias de srcId.
				//on recommence la recherche avec le "bon" srcRef.
				roadEnd.door.closeFromHouse();
				return this._getHouse(wsp, SRC.srcRef(srcFields));
			}
			if (!wsp.isAvailable) await wsp.waitForAvailable(null);
			const itemType = wsp.wspMetaUi.getItemType(srcFields.itModel);
			initWspXmlHouse(house, wsp, itemType, wspRefFound, srcFields);
			const schema = await itemType.getSchema();
			const writable = (srcFields.srcRi & ESrcRights.write) == ESrcRights.write;
			house.resetSchema(schema, null, {autoMutate: writable, autoComplete: writable, genAnnots: true}); //, autoNormXml: writable, autoNormChars: writable non car pourrait perturber une édition en cours dans une autre win (sera vrai surtout en édition collab).
			return house;
		});
		if (h !== house) return h;
		//TODO Check locks externals
		this._addHouse(house);
		return house;
	}

	/** NE PAS UTILISER. cf _getHouse() */
	private async __fetchHouse(wsp: Wsp, srcUriOrId: srcUri | srcId, wspUriOrId: wspUri | wspId): Promise<IWspXmlHouse | null> {
		const qs = IO.qs("format", "stream", "param", wsp.code, "refUri", srcUriOrId, "fields", this.getSrcFieldNames(srcUriOrId), "204", "");//, "transform", "transform=facet&facet=xml&opt" //, "srcTrashed", "false"
		const resp = await this.wspServer.config.wspSrcUrl.fetch(qs, 'dom');
		if (!resp.ok || resp.status === EHttpStatusCode.noContent) {
			if (resp.status === EHttpStatusCode.noContent || resp.status === EHttpStatusCode.notFound) {
				resp.asDom = DOM.newDomDoc();
			} else {
				console.log("Get XML failed for " + srcUriOrId, resp.error);
				return null;
			}
		} else if (!DOM.isDomValid(resp.asDom)) {
			console.log("Invalid XML for " + srcUriOrId, Error("InvalidXml"));
			return null;
		}
		const fields = resp.headers.get("X-SCFIELDS");
		if (!fields) return null;
		const srcFields = JSON.parse(fields) as JSrcFields;
		//console.log(":::wspRef diff=", wspRefFound !== wspRef, "asked", wspRef, " found ", srcFields);
		if (SRC.isSrcId(srcUriOrId) && srcUriOrId !== srcFields.srcId) {
			//Risque de créer 2 houses en doublon car le wspRef initial était un alias de srcId.
			//on recommence la recherche avec le "bon" srcRef.
			return this._getHouse(wsp, SRC.srcRef(srcFields));
		}
		const wspRefFound = WSP.buildWspRef(wsp.code, srcFields);
		if (!wsp.isAvailable) await wsp.waitForAvailable(null);
		const itemType = wsp.wspMetaUi.getItemType(srcFields.itModel);
		const schema = await itemType.getSchema();
		const doc = DOM.cleanupDom(resp.asDom, true, false, true) as IDocumentInWspHouse;
		if (!WspXmlHouseCstr) WspXmlHouseCstr = (await import("lib/wsp/wspHouse.js")).WspXmlHouse;
		const house = new WspXmlHouseCstr({
			id: wspRefFound,
			initialDoc: doc,
			schema: schema,
			customInit: function (this: IWspXmlHouse) {initWspXmlHouse(this, wsp, itemType, wspRefFound, srcFields)},
			// TODO autoCleanup en pref du user.
			buildOptions: {autoMutate: true, autoComplete: true, autoNormXml: true, autoNormChars: true, genAnnots: true},
			historyMinEntries: 300
		}) as IWspXmlHouse;

		house.listeners.on('dirtyChange', (dirty: boolean) => {
			if (house.isMasterRoot()) {
				//isMasterRoot toujours vrai à linit, mais pourrait devenir slave par un passage en coédition.
				if (dirty) {
					this._wsFrame.postMsg({svc: 'wspWorking', type: 'state', uri: house.wspRef, state: EWspWorkingSt.w} as JWspWorkingMsgStateOut);
					if (!house.saveTimer) {
						const saveInterval = house.itemType.reg.getPref("item.saveInterval", 10000);
						if (saveInterval) house.saveTimer = setTimeout(() => {
							house.saveTimer = 0;
							this._saveHouse(house);
						}, saveInterval);
					}
				} else {
					//on retourne au status none ou read.
					if (house.saveTimer) clearTimeout(house.saveTimer);
					this._wsFrame.postMsg({svc: 'wspWorking', type: 'state', uri: house.wspRef, state: this._getMergedServerStateFor(house.wspRef)} as JWspWorkingMsgStateOut);
				}
			}
			for (const pl of this._places) {
				if (pl.isConcernedByWsp(house.wsp.code)) pl.eventsMgr.emitCatched('houseDirtyChange', house.wspRef, house.wsp, house.srcFields, dirty);
			}
		});

		if (this._connectionReady) {
			house.states = await this._getStates(house.wspRef, false, null);
			let writeState: JWspWorkingState;
			if (house.states && (writeState = house.states.find((st) => {return st.state === EWspWorkingSt.w}))) {
				this._lockHouse(house, writeState.clId, writeState.account);
			} else if (this._lockedExternals.has(house.wspRef)) {
				this._lockHouse(house, ".", this.wspServer.reg.env.universe.auth.currentAccount);
			}
		} else if (this._lockedExternals.has(house.wspRef)) {
			this._lockHouse(house, ".", this.wspServer.reg.env.universe.auth.currentAccount);
		}
		this._addHouse(house);

		return house;
	}

	getHouseIfFetched(wspRef: wspRef): IWspXmlHouse {
		return this._houses.get(wspRef);
	}

	/**
	 * Création d'une house locale non rattachée directement à un item, mais rattaché à un wsp et
	 * pouvant posséder des liens vers des items (qui seront rafraichis).
	 * Usages : création de structures XML "volantes" avant dêtre rattachées (cf Metas des balises de texte).
	 * IMPORTANT : penser à la supprimer une fois son usage terminé : @see removeLocalHouse()
	 */
	async newLocalHouse(config: OXmlTypedHouseConfig, wsp: Wsp): Promise<IWspXmlHouse> {
		if (!WspXmlHouseCstr) WspXmlHouseCstr = (await import("lib/wsp/wspHouse.js")).WspXmlHouse;
		config.id = "local:" + this._localId++;
		if (config.initialJml) config.initialDoc = JML.jmlToDom(config.initialJml);
		if (!config.initialDoc) config.initialDoc = DOM.newDomDoc();
		const doc = (config.initialDoc.ownerDocument || config.initialDoc) as IDocumentInWspHouse;
		doc.wsp = wsp;
		doc.srcRefMap = new Map();
		const house = new WspXmlHouseCstr(config) as IWspXmlHouse;
		house.wsp = wsp;
		this._houses.set(house.id, house);
		return house;
	}

	_localId = 1;

	removeLocalHouse(house: IWspXmlHouse) {
		house.killed = true;
		this._houses.delete(house.id);
	}

	/** Enregistre une house si elle est dirty et master. */
	async _saveHouse(house: IWspXmlHouse, from?: WspsLivePlace): Promise<void> {
		if (house.saveTimer) {
			clearTimeout(house.saveTimer);
			house.saveTimer = 0;
		}
		if (!house.isDirty || house.killed) return;
		if (!house.isMasterRoot()) {
			try {
				await house.listeners.emitAsync('onSave', 'saving');
				await house.publicDoor.receiveRequest(new AskForSaveReq());
				await house.listeners.emitAsync('onSave', 'saved');
			} catch (e) {
				await house.listeners.emitAsync('onSave', 'saveFailed', e);
				if (!house.killed) house.setDirty(true);
				throw e;
			}
			return;
		}
		const wsp = house.wsp;
		const srcRef = SRC.srcRef(house.srcFields);
		const qs = IO.qs("cdaction", "PutSrc", "param", wsp.code, "refUri", srcRef, "clId", wsp.wspServer.chain.clId, "fields", this.getSrcFieldNames(srcRef));
		try {
			house.setDirty(false);
			await house.listeners.emitAsync('onSave', 'saving');
			const desc = await wsp.wspServer.config.wspSrcUrl.fetchJson<JSrcFields>(qs, {method: "PUT", body: DOM.ser(house.getDocumentForSave(), true)});
			if (!desc) throw Error("_saveHouse result desc is null");
			house.srcFields = desc;
			if (ITEM.getSrcUriType(desc.srcUri) === "res") {
				const itemFields = await wsp.fetchShortDesc(ITEM.extractItemUri(desc.srcUri));
				this.dispatchLocalChange(house, wsp, itemFields, EWspChangesEvts.u, itemFields);
			} else {
				this.dispatchLocalChange(house, wsp, desc, EWspChangesEvts.u, desc);
			}
			await house.listeners.emitAsync('onSave', 'saved');
		} catch (e) {
			await house.listeners.emitAsync('onSave', 'saveFailed', e);
			if (!house.killed) house.setDirty(true);
			throw e;
		}
	}

	async saveHouse(wspRef: wspRef): Promise<void> {
		const h = this.getHouseIfFetched(wspRef);
		if (h && h.isDirty) await this._saveHouse(h);
	}

	async saveAllHouses() {
		try {
			const waitFor = [] as Promise<any>[];
			for (const house of this._allHouses) {
				if (house.isDirty) waitFor.push(this._saveHouse(house));
			}
			this.cmEditors?.saveAll(waitFor);
			if (waitFor.length > 0) await Promise.all(waitFor);
		} catch (e) {
			ERROR.log("Save houses failed", e);
		}
	}

	async saveItems(wspCd: wspCd, itemIdents: JSrcIdent[]) {
		const waiters: Promise<any>[] = [];
		for (const srcIdent of itemIdents) {
			const wspRef = WSP.buildWspRef(wspCd, srcIdent);
			const h = this.getHouseIfFetched(wspRef);
			if (h && h.isDirty) waiters.push(this._saveHouse(h));
			else this.cmEditors?.save(srcIdent.srcUri, waiters);
		}
		return waiters.length > 0 ? Promise.all(waiters) : null;
	}

	async reloadHouse(wspRef: wspRef): Promise<void> {
		const h = this.getHouseIfFetched(wspRef);
		if (h) {
			await this._saveHouse(h);
			return this._reloadHouse(h);
		}
		return this.cmEditors?.refetchContent(wspRef); //Pas de house, peut-être un cmEditor.
	}

	/** Un chgt externe a été détecté, on réinitialise cette house. */
	async _reloadHouse(house: IWspXmlHouse): Promise<void> {
		if (!house.isMasterRoot() || house.killed) return; //pas de reload sur une house slave (sera géré par le master)
		//FIXME si on a des slaves ? if(house.otherDoors.length > 0) ... reload interne à la House nécessaire ou switch des doors ? à rapprocher du transfert du master ?
		if (house.isDirty) house.isDirty = false;
		this._removeHouse(house);
		//on tue cette house pour bloquer tout save asynchrone.
		house.killed = true;
		if (house.publicDoors.length > 0) {
			const newHouse = await this._getHouse(house.wsp, SRC.srcRef(house.srcFields));
			if (!newHouse) throw Error("Reload house failed: " + SRC.srcRef(house.srcFields));
			if (house.lockedBy) {
				//l'ancienne house avait un lock, on dispatch le unlock, sera réactivé après le switch avec la nouvelle house si lock pas issu du serveur.
				this._unlockHouse(house);
			}
			for (const place of this._places) place._switchHouse(house, newHouse);
			if (newHouse.lockedBy) newHouse.listeners.emit("onLock", true); //lock sur la nouvelle house, on dispatch.
		}
	}

	protected _cleanupTimer: any;
	protected _cleanupHandler: () => void;

	_addHouse(house: IWspXmlHouse) {
		this._allHouses.add(house);
		this._houses.set(house.wspUri, house);
		if (house.wspId) this._houses.set(house.wspId, house); //indexation double wspUri ET wspId
		if (house.wspUriItem) this._houses.set(house.wspUriItem, house); //indexation triple sur l'uri de l'item.
		if (this._allHouses.size > this.cleanupHousesThreshold) {
			if (!this._cleanupHandler) this._cleanupHandler = this._doCleanupHandler.bind(this);
			if (this._allHouses.size > this.cleanupHousesNowThreshold) {
				if (this._cleanupTimer) {
					clearTimeout(this._cleanupTimer);
					this._cleanupTimer = 0;
				}
				this._cleanupHandler();
			} else {
				if (!this._cleanupTimer) this._cleanupTimer = setTimeout(this._cleanupHandler, 1000);
			}
		}
	}

	_doCleanupHandler() {
		const countHouseBefore = this._allHouses.size;
		let houseToClean: IWspXmlHouse;
		let olderTime = countHouseBefore > this.cleanupHousesNowThreshold ? Date.now() : Date.now() - 60_000;
		do {
			for (const house of this._allHouses) {
				if (house.notUsedSince > 0 && house.notUsedSince < olderTime) {
					olderTime = house.notUsedSince;
					houseToClean = house;
				}
			}
			if (houseToClean) {
				this._removeHouse(houseToClean);
				this._wsFrame.postMsg({svc: 'wspWorking', type: 'state', uri: houseToClean.wspRef, state: EWspWorkingSt.none});
			}
			if (this._allHouses.size > this.cleanupHousesNowThreshold) {
				if (houseToClean) {
					//On poursuit le cleanup
					olderTime = Date.now();
					continue;
				}
				//aïe, on reste > à this.cleanupHousesNowThreshold memLeak quelque-part ?
				ERROR.log(`Too many houses in use: ${this._allHouses.size} / ${this.cleanupHousesNowThreshold}`);
			}
			//Si on n'est pas en modes 'urgence' cleanupHousesNowThreshold, on élimine les houses une à une à chaque cycle
			break;
		} while (true);
		//console.log(`Clean houses : before=${countHouseBefore} after=${this._allHouses.size}`);
		if (this._allHouses.size > this.cleanupHousesThreshold) this._cleanupTimer = setTimeout(this._cleanupHandler, 10_000);
	}

	_removeHouse(house: IWspXmlHouse) {
		this._allHouses.delete(house);
		this._houses.delete(house.wspUri);
		if (house.wspId) this._houses.delete(house.wspId);
		if (house.wspUriItem) this._houses.delete(house.wspUriItem);
		house.wsp?.stopListenChanges()
	}

	_lockHouse(house: IWspXmlHouse, byClId: string, byAccount: string) {
		if (house.isDirty) {
			//Conflit (race condition) !
			//reload de la house
			this._reloadHouse(house);
			return;
		}
		if (!house.lockedBy || house.lockedBy.clId !== byClId || house.lockedBy.account !== byAccount) {
			house.lockedBy = {clId: byClId, account: byAccount};
			house.listeners.emit("onLock", true);
		}
	}

	_unlockHouse(house: IWspXmlHouse) {
		house.lockedBy = null;
		house.listeners.emit("onLock", false);
	}

	/**
	 * Impose un lock externe : la house n'est plus éditable, mais vu du serveur, c'est nous qui détenons le lock.
	 * Utilisé par les dynGen pour l'édition des commentaires.
	 */
	_lockExternals(wspRef: wspRef): boolean {
		if (this._lockedExternals.has(wspRef)) return false;
		const house = this._houses.get(wspRef);
		if (house) {
			if (house.isDirty || house.lockedBy) return false;
			//On crée un 'faux' lock sur la house pour focer les éditeurs en readOnly.
			this._lockHouse(house, ".", this.wspServer.reg.env.universe.auth.currentAccount);
		}
		if (this.cmEditors?.tryLockExternals(wspRef) === false) return false;
		this._lockedExternals.add(wspRef);
		this._wsFrame.postMsg({svc: 'wspWorking', type: 'state', uri: wspRef, state: EWspWorkingSt.w} as JWspWorkingMsgStateOut);
		return true;
	}

	_unlockExternals(wspRef: wspRef) {
		this._lockedExternals.delete(wspRef);
		const house = this._houses.get(wspRef);
		if (house) this._unlockHouse(house);
		this.cmEditors?.unlockExternals(wspRef);
		this._wsFrame.postMsg({svc: 'wspWorking', type: 'state', uri: wspRef, state: this._getMergedServerStateFor(wspRef)} as JWspWorkingMsgStateOut);
	}

	/** Mémoire des locks externes posés. */
	protected _lockedExternals = new Set<wspRef>();

	/**
	 * true si le service est disponible et la connection opérationnelle.
	 * Interne, car retour synchrone (donc non proxiable dans un SharedWorker).
	 */
	get _connectionReady(): boolean {return this.isAvailable && this._wsFrame.wsState == EWsState.wsOpened}

	async _getStates(wspRef: wspRef, fromCache: boolean, from: WspsLivePlace): Promise<JWspWorkingState[]> {
		if (fromCache) {
			const house = this._houses.get(wspRef);
			if (house) return house.states || [];
		}
		if (!this._connectionReady && !await this.waitForReady()) throw Error("Not connected");
		//On remonte les états distants.
		return (await this._wsFrame.sendReq<JWspWorkingStatesResp>({svc: 'wspWorking', type: 'states', uri: wspRef})).states;
	}

	sendMsg(m: Jsonisable) {
		if (this._initPendingOut) this._initPendingOut.push(m);
		else this._wsFrame.postMsg(m);
	}

	protected onWspWorkingMsg(m: JWspWorkingInitIn | JWspWorkingMsgStateIn | JWspWorkingStatesResp): void {
		if (WspLiveDEBUG) console.log("->WspsLive", m);
		if (isWsMsgError(m)) {
			if (m.type === 'init') {
				this.setAvailable(false);
			}
			return;
		}

		switch (m.type) {
		case 'init': {
			this.onWspWorkingInitMsg(m.uris || {});
			break;
		}

		case 'state': {
			if (this._initPendingIn) this._initPendingIn.push(m);
			else this.onWspWorkingStateMsg(m);
			break;
		}
		}
	}

	protected async onWspWorkingInitMsg(urisStates: JWspWorkingInitStates) {
		//On envoie ce qui est était en attente
		if (this._initPendingOut) {
			for (const m of this._initPendingOut) this._wsFrame.sendMsg(m);
			this._initPendingOut = null;
		}
		//On informe que la connection est opérationnelle
		this._initPendingIn = []; //on bufferise les msg entrant le temps de l'init des wsp.
		//On refresh les ateliers.
		for (const wsp of this._wsps) await wsp.onWsConnectionRenewed(urisStates).catch(console.log);
		//On refresh le statut lock des houses ouvertes
		for (const uri in urisStates) {
			const house = this._houses.get(uri);
			if (house) {
				//une house existe...
				const uriStates = urisStates[uri];
				if (uriStates.reject || uriStates.srcStamp ? uriStates.srcStamp !== house.srcFields.srcStamp : uriStates.srcDt !== house.srcFields.srcDt) {
					//édition concurrente ou chgt de droits pendant la rupture de la connection WebSocket
					//XXX dispatch info avant reload ? Si dirty, createItemConflict , copie presse-papier ?
					await this._reloadHouse(house);
				} else {
					house.states = uriStates.states;
					const writeState = house.states ? house.states.find((st) => {return st.state === EWspWorkingSt.w}) : null;
					if (writeState) this._lockHouse(house, writeState.clId, writeState.account);
					else if (house.lockedBy != null) this._unlockHouse(house);
				}
			}
		}

		if (this.cmEditors) await this.cmEditors.onWspWorkingInitMsg(urisStates);

		//On informe les places.
		for (const place of this._places) {
			place.eventsMgr.emitCatched('onConnectionRenewed', urisStates);
		}
		//On diffuse les msg issus du serveur arrivés pendant wsp.onWsConnectionRenewed()
		const initPendingIn = this._initPendingIn;
		this._initPendingIn = undefined;
		for (const m of initPendingIn) {
			if (m.svc === 'wspWorking') this.onWspWorkingMsg(m);
			else this.onWspChangesMsg(m);
		}
	}

	protected async onMoanMsg(msg: JMoanServerMsg) {
		let wspRef = msg.houseId;
		if (wspRef) {
			if (wspRef[0] === '#') {
				//CommentHouse...
				const cmtHouseId = wspRef;
				wspRef = wspRef.substring(wspRef.indexOf('#', 1) + 1);
				const house = this._houses.get(wspRef);
				if (house) {
					const cmtH = (house.getStateById(CommentsState.CMTSTATE_ID) as CommentsState)?.getCommentHouseById(cmtHouseId);
					cmtH?.masterDoor?.roadEnd.listenIncoming(House.MSG_FACTORYREG.newFromJson(msg));
				}
			} else {
				const house = this._houses.get(wspRef);
				if (house) house.masterDoor?.roadEnd.listenIncoming(House.MSG_FACTORYREG.newFromJson(msg))
				else if (msg.type === "initSlaveRep" || msg.type === "ack") this._initingSlaveHouses.get(wspRef)?.masterDoor?.roadEnd.listenIncoming(House.MSG_FACTORYREG.newFromJson(msg));
			}
		}
	}

	/** Messages émis pendant l'init asynchrone de la connection. */
	protected _initPendingOut: Jsonisable[];
	/** Messages reçus pendant l'init asynchrone de la connection dû au rechargement des ateliers. */
	protected _initPendingIn: Jsonisable[];

	protected onWspWorkingStateMsg(m: JWspWorkingMsgStateIn) {
		const wspCd = WSP.extractWspCdFromWspRef(m.uri);
		const srcRef = WSP.extractSrcRefFromWspRef(m.uri);
		//WorkingSt si la house correspondant est chargée.
		const house = this._houses.get(m.uri);
		if (house) {
			//MAJ de la liste des states des autres devices
			if (m.state === EWspWorkingSt.none) {
				if (house.states) {
					const idx = house.states.findIndex((st) => {return st.clId === m.clId});
					if (idx >= 0) house.states.splice(idx, 1);
				}
			} else {
				if (house.states) {
					const idx = house.states.findIndex((st) => {return st.clId === m.clId});
					if (idx >= 0) house.states[idx].state = m.state;
				} else {
					house.states = [m];
				}
			}
			//Gestion du lock.
			if (m.state === EWspWorkingSt.w) {
				this._lockHouse(house, m.clId, m.account);
			} else {
				if (house.lockedBy && house.lockedBy.clId === m.clId) {
					this._unlockHouse(house);
				}
			}
		}

		this.cmEditors?.onWspWorkingStateMsg(m);

		//Notif les places
		for (const place of this._places) {
			if (place.isConcernedByWsp(wspCd)) place.eventsMgr.emitCatched('newWspWorkingState', m.uri, wspCd, srcRef, m.state, m.account, m.clId);
		}
	}

	protected async onWspChangesMsg(m: JWspStChangeMsg | JWspUriChangeMsg) {
		if (this._initPendingIn) this._initPendingIn.push(m);
		else {
			//TODO pooling events this._poolChangesEvts.push(m.uri, m.type);
			if ('wspSt' in m) {
				//Modif du statut de l'atelier ou de ses props shortDesc dont perms.
				return this.onWspChange(m);
			} else if (m.type === EWspChangesEvts.perm) {
				//Chgt de permission.
				const srcUri = m.srcUri;
				if (srcUri === "") {
					//Refresh des props de l'atelier
					// NOTE : recherche des wsps concernés, puis _onWspPermChange en deux étapes car raceCond : _onWspPermChange => évol de la liste this._wsps
					const changesWsps: Wsp[] = [];
					this._wsps.forEach((wsp) => wsp.code === m.wspCd ? changesWsps.push(wsp) : null);
					if (changesWsps && changesWsps.length)
						try {
							await Promise.all(changesWsps.map(async (wsp) => wsp._onWspPermChange(m)));
						} catch (e) {
							console.log(e);
						}
				}

				//Rééval des perms de toutes les houses descendantes.
				const housesToFetchFields: IWspXmlHouse[] = [];
				for (const house of this._allHouses) {
					if (house.wsp.code === m.wspCd && SRC.isSubUriOrEqual(srcUri, house.srcFields.srcUri)) {
						housesToFetchFields.push(house);
					}
				}
				if (housesToFetchFields.length > 0) {
					//console.log("srcRefsToFetch:::", housesToFetchFields);
					const wsp = housesToFetchFields[0].wsp;
					const data = new FormData();
					data.append("refUris", housesToFetchFields.map((house) => SRC.srcRef(house.srcFields)).join('\t'));
					const srcs = await wsp.wspServer.config.wspSrcUrl.fetchJson<JSrcFields[]>(IO.qs("param", wsp.code, "fields", "srcRi*srcRoles"), {method: 'POST', body: data});
					for (let i = 0; i < housesToFetchFields.length; i++) {
						const h = housesToFetchFields[i];
						h.srcFields.srcRi = srcs[i].srcRi;
						h.srcFields.srcRoles = srcs[i].srcRoles;
						h.listeners.emitCatched("onPermChange", h);
					}
				}

				await this.cmEditors?.onWspUriChangeMsg(m);

				//Dispatch dans les places.
				for (const place of this._places) {
					if (place.isConcernedByWsp(m.wspCd)) place.eventsMgr.emitCatched('wspUriChange', m, 'server');
				}
			} else {
				if (m.type === EWspChangesEvts.u) {
					//Update de la house correspondante.
					await this.onUriChange(m.wspCd, m.srcUri);
				} else if (m.type === EWspChangesEvts.r) {
					await this.onUriRemoved(m.wspCd, m.srcUri);
				} else if (m.type === EWspChangesEvts.lcSt || m.type === EWspChangesEvts.rspUsrs) {
					const house = this._houses.get(WSP.buildWspUri(m.wspCd, m.srcUri)/*WSP.buildWspRef(m.wspCd, m) Pour les metas des res : seul le srcUri de l'item est indexée. A revoir après XulRunner ? */);
					if (house != null) {
						house.srcFields.srcRoles = (await house.wsp.fetchShortDesc(SRC.srcRef(m))).srcRoles;
						house.listeners.emitCatched("onPermChange", house);
					}
				}

				if (this.cmEditors) await this.cmEditors.onWspUriChangeMsg(m);

				if (m.type === EWspChangesEvts.u || m.type === EWspChangesEvts.r) {
					await this.revalidLinks(m);
				}

				//Dispatch dans les places.
				for (const place of this._places) {
					if (place.isConcernedByWsp(m.wspCd)) place.eventsMgr.emitCatched('wspUriChange', m, 'server');
				}
			}
		}
	}

	private async onUriChange(wspCd: wspCd, srcRef: srcRef) {
		//Update de la house correspondante.
		const house = this._houses.get(WSP.buildWspUri(wspCd, srcRef)/*WSP.buildWspRef(m.wspCd, m) Pour les metas des res : seul le srcUri de l'item est indexée. A revoir après XulRunner ? */);
		if (house != null) try {
			let reload = true;
			if (house.wspUriItem) {
				//la house édite des metas de l'item (res), donc cet evt d'update pourrait concerner d'autres flux
				//on controle avant que le srcStamp de ces metas a bien changé !
				try {
					const newSt = await this.wspServer.config.wspSrcUrl.fetchJson<JSrcFields>(IO.qs("param", wspCd, "refUri", SRC.srcRef(house.srcFields), "fields", "srcStamp*srcDt"));
					reload = newSt.srcStamp ? house.srcFields.srcStamp !== newSt.srcStamp : house.srcFields.srcDt !== newSt.srcDt;
				} catch (e) {
					console.error(e);
				}
			}
			if (reload) await this._reloadHouse(house);
		} catch (e) {
			//echec au (re)chargement de cette house.
			console.error(e);
		}
	}

	private onUriRemoved(wspCd: wspCd, srcUri: srcUri) {
		//Cleanup de cet éditeur des maps internes (note: house non killed, à voir...)
		if (ITEM.getSrcUriType(srcUri) === "space") {
			for (const house of this._allHouses) {
				if (house.wsp.code === wspCd && SRC.isSubUriOrEqual(srcUri, house.srcFields.srcUri)) {
					try {
						this._removeHouse(house);
					} catch (e) {
						console.error(e);
					}
				}
			}
		} else {
			const house = this._houses.get(WSP.buildWspUri(wspCd, srcUri)/*WSP.buildWspRef(m.wspCd, m) Pour les metas des res : seul le srcUri de l'item est indexée. A revoir après XulRunner ? */);
			if (house != null) try {
				this._removeHouse(house);
			} catch (e) {
				console.error(e);
			}
		}
	}

	private async revalidLinks(m: JWspUriChangeMsg) {
		//Controle des liens de chaque house pour les revalider.
		if (!revalidLink) revalidLink = (await import("back/wsp/widgets/wed/schemaMetaWsp.js")).revalidLink;
		const isById = m.srcId != null;
		if (ITEM.getSrcUriType(m.srcUri) === "space") {
			if (m.type !== EWspChangesEvts.r) return;
			//Gestion de la suppression d'un espace
			const mapToRefresh = new Map<Wsp, Map<srcRef, IWspXmlHouse[]>>()
			for (const house of this._allHouses) {
				for (let src of (house.document as IDocumentInWspHouse).srcRefMap.values()) {
					if (src && ("srcUri" in src)) {
						if (SRC.isSubUri(m.srcUri, src.srcUri)) {
							//Approximation ici si foreignItem : on pourrait provoquer un refresh sur un item d'un autre atelier : pas grave.
							let srcs = mapToRefresh.get(house.wsp);
							if (!srcs) mapToRefresh.set(house.wsp, (srcs = new Map()));
							const srcRef = SRC.srcRef(src);
							let houses = srcs.get(srcRef);
							if (!houses) srcs.set(srcRef, (houses = []));
							houses.push(house);
						}
					}
				}
			}
			mapToRefresh.forEach((refs, wsp) => {
				refs.forEach((houses, srcRef) => {
					wsp.fetchShortDescSubItems(srcRef).then((shortDesc) => {
						const newShortDesc = shortDesc.srcSt > 0 ? shortDesc : null;
						for (const house of houses) {
							if (!house.killed) revalidLink(house, srcRef, newShortDesc);
						}
					});
				})
			});
		} else {
			let housesWithLink: IWspXmlHouse[]; //Liste des houses qui pointent cet item modifié
			const srcRef = SRC.srcRef(m);
			for (const house of this._allHouses) {
				if (isById || house.wsp.code === m.wspCd) {
					//isById = items étrangers possibles (donc wsp différent)
					const shortDesc = (house.document as IDocumentInWspHouse).srcRefMap.get(srcRef);
					if (shortDesc !== undefined) {
						//ce srcRef est référencé par cette house.
						if (m.type === EWspChangesEvts.r) {
							//item supprimé
							if (shortDesc !== null) revalidLink(house, srcRef, null); //nouvel état, on refresh les annots.
						} else {
							if (housesWithLink) housesWithLink.push(house);
							else housesWithLink = [house];
						}
					}
				}
			}
			if (housesWithLink) {
				this.findWsp(m.wspCd, true).fetchShortDescSubItems(srcRef).then((shortDesc) => {
					const newShortDesc = shortDesc.srcSt > 0 ? shortDesc : null;
					for (const house of housesWithLink) {
						if (!house.killed) revalidLink(house, srcRef, newShortDesc);
					}
				});
			}
		}
	}

	protected onWspChange(m: JWspStChangeMsg) {
		for (const wsp of this._wsps) if (wsp.code === m.wspCd) {
			try {
				wsp._onWspChange(m);
			} catch (e) {
				console.log(e);
			}
		}
		if (m.wspSt == "loaded" || m.wspSt == "migratingDone" || m.wspSt == "migratingFailed") {
			for (let h of this._allHouses) {
				if (h.wsp.code === m.wspCd) {
					this._saveHouse(h);
					this._reloadHouse(h);
				}
			}
		}
	}

	protected onWsOpened() {
		if (WspLiveDEBUG) console.log("onWsOpened");
		this._initPendingOut = [];
		this.setAvailable(true);
		// Init des wspWorking
		const m = {svc: 'wspWorking', type: 'init'} as any;
		const mergedStates = new Map<wspRef, EWspWorkingSt>();
		for (const wsp of this._wsps) mergedStates.set(wsp.code, EWspWorkingSt.r);
		for (const house of this._allHouses.values()) {
			mergedStates.set(house.wspRef, house.isDirty && house.isMasterRoot() ? EWspWorkingSt.w : EWspWorkingSt.l);
		}
		for (const place of this._places) {
			for (const [wspRef, st] of place._wspRefs.entries()) {
				const mergedSt = mergedStates.get(wspRef);
				if (WspsLive.getHigherState(mergedSt, st) !== mergedSt) mergedStates.set(wspRef, st);
			}
		}
		if (mergedStates.size > 0) {
			m.uris = {};
			for (const [uri, st] of mergedStates.entries()) m.uris[uri] = st;
		}
		this._wsFrame.sendMsg(m);
		// Ecoutes des wspChanges
		if (this._wsps.size > 0) {
			const wspCodes = new Set<string>();
			this._wsps.forEach((w) => {wspCodes.add(w.code)});
			const m2 = {svc: 'wspChanges', type: 'listen', wsps: Array.from(wspCodes)} as any;
			this._wsFrame.sendMsg(m2);
		}
		//Rechargement des houses slaves
		for (const house of this._allHouses.values()) {
			if (!house.isMasterRoot()) house.initFromMaster();
		}
	}

	protected onWsClosed(code: number, reason: string, wasClean: boolean) {
		if (WspLiveDEBUG) console.log("onWsClosed");
		for (const place of this._places) {
			place.eventsMgr.emitCatched('onConnectionLost', code, reason, wasClean);
		}
	}

	protected startIfNeeded() {
		if (this.isAvailable !== false && this._wsFrame.wsState !== EWsState.wsOpened) this._wsFrame.startWs();
	}

	protected setAvailable(available: boolean) {
		this.isAvailable = available;
		if (this._pendingReady) {
			for (const r of this._pendingReady) r(available);
			this._pendingReady = undefined;
		}
	}

	_onPlaceClose(place: WspsLivePlace) {
		this._places.delete(place);
		for (const [wspRef, st] of place._wspRefs.entries()) {
			const newSt = this._getMergedServerStateFor(wspRef);
			if (WspsLive.getHigherState(newSt, st) !== newSt) {
				//Si le state disparu était seul à l'origine de l'ancien higherState, on a donc provoqué un
				// changement d'état global qui interresse le serveur
				this.sendMsg({svc: 'wspWorking', type: 'state', uri: wspRef, state: newSt} as JWspWorkingMsgStateOut);
			}
		}
	}

	//todo cleanup en 5.1 après suppr XulRunner -> remontée systématique wspChanges du serveur.
	//=> NON car pb retour graphique mal maitrisé avec event async WS.
	async dispatchLocalChange(from: WspsLivePlace | IWspXmlHouse | null, wsp: Wsp, desc: JSrcIdent, event: EWspChangesEvts, shortDesc?: JSrcFields) {
		//Les wspChanges doivent être ramenés sur les items, pas sur les res des items multi-flux.
		if (ITEM.getSrcUriType(desc.srcUri) === "res") throw Error("_dispatchLocalChange must be called on item");
		const m = {
			svc: 'wspChanges',
			type: event,
			wspCd: wsp.code,
			srcUri: desc.srcUri,
			srcId: desc.srcId,
			shortDesc: shortDesc,
		} as JWspUriChangeMsg;
		if (m.type === EWspChangesEvts.u && (!from || from instanceof WspsLivePlace)) await this.onUriChange(m.wspCd, m.srcUri); //cas édition externe d'une source (ex: création qui écrase un précédent contenu en corbeille mais avec house en cache).
		if (m.type === EWspChangesEvts.r) this.onUriRemoved(m.wspCd, m.srcUri);
		if (this.cmEditors) await this.cmEditors.onWspUriChangeMsg(m);
		if (m.type === EWspChangesEvts.u || m.type === EWspChangesEvts.r) await this.revalidLinks(m);
		for (const place of this._places) {
			if (place.isConcernedByWsp(wsp.code)) place.eventsMgr.emitCatched('wspUriChange', m, from || 'local');
		}
	}

	_getMergedServerStateFor(wspRef: wspRef, exclude?: WspsLivePlace): EWspWorkingSt {
		const house = this._houses.get(wspRef);
		if (house && house.isDirty && house.isMasterRoot()) return EWspWorkingSt.w;
		if (this.cmEditors?.isDirty(wspRef)) return EWspWorkingSt.w;
		if (this._lockedExternals.has(wspRef)) return EWspWorkingSt.w;
		let state: EWspWorkingSt = house ? EWspWorkingSt.l : EWspWorkingSt.none;
		for (const place of this._places) {
			if (place === exclude) continue;
			const st = place._wspRefs.get(wspRef);
			if (st) {
				if (st === EWspWorkingSt.w) return st;
				if (st === EWspWorkingSt.r) state = st;
				else if (st === EWspWorkingSt.l && state === EWspWorkingSt.none) state = st;
			}
		}
		return state;
	}

	static getHigherState(st1: EWspWorkingSt, st2: EWspWorkingSt): EWspWorkingSt {
		if (!st1) return st2;
		if (!st2) return st1;
		//'' < 'l' < 'r' < 'w'
		return st1 < st2 ? st2 : st1;
	}

	getSrcFieldNames(srcUri: srcUri): string {
		return ITEM.isHistoryOrTrashUri(srcUri) ? HOUSE_SRCFIELDS_HISTO : HOUSE_SRCFIELDS;
	}
}

function initWspXmlHouse(house: IWspXmlHouse, wsp: Wsp, itemType: ItemType, wspRef: wspRef, srcFields: JSrcFields) {
	wsp.listenChanges()
	house.wsp = wsp;
	house.itemType = itemType;
	house.srcFields = srcFields;
	if (srcFields.srcId) {
		house.wspId = wspRef;
		house.wspUri = WSP.buildWspUri(wsp.code, srcFields.srcUri);
	} else {
		house.wspUri = wspRef;
	}
	house.wspRef = house.wspId || house.wspUri;
	if (ITEM.getSrcUriType(house.wspUri) === "res") house.wspUriItem = ITEM.extractItemUri(house.wspUri);
}

const HOUSE_SRCFIELDS = "srcUri*srcId*itModel*srcStamp*srcDt*srcSt*srcRoles*srcRi";
const HOUSE_SRCFIELDS_HISTO = "srcUri*srcId*itModel*srcStamp*srcDt*srcSt*srcRoles*srcRi*srcLiveUri";

//Lazy loading OT libs
let WspXmlHouseCstr: typeof WspXmlHouse;
let WspXmlSlaveHouseCstr: typeof WspXmlSlaveHouse;
let revalidLink: (house: IWspXmlHouse, srcRef: srcRef, newShortDesc: JSrcFields) => void;


/**
 * A utiliser par un composant graphique pour dialoguer avec le svc WspServer.wspLive.
 * Objet obtenu via WspLive.newPlace()
 */
export class WspsLivePlace {

	/** Gestionnaire des évènements de la place. */
	eventsMgr = new EventsMgr<IWspLivePlaceEvents>();

	/** Status des wspRefs pour cette place. */
	_wspRefs = new Map<wspRef, EWspWorkingSt>();

	/** DocHolders détenus par cette place. */
	_docHolders = new Set<IPlaceDocHolder>();

	/** Wsp détenus par cette place. */
	_wsps = new Map<wspCd, Wsp>();

	protected _lockExternals: Set<wspRef>;

	constructor(public readonly wspsLive: WspsLive) {
		wspsLive._places.add(this);
	}

	waitForReady(): Promise<boolean> {return this.wspsLive.waitForReady()}

	/**
	 * Obtient un Wsp qui est enregsitré et mis à jour automatiquement.
	 * Note : une fois cet atelier obtenu par cette méthode, il ne sera jamais
	 * déréférencé jusqu'à la fermeture de cette place.
	 */
	getWsp(wspCd: wspCd): Wsp {
		let wsp = this._wsps.get(wspCd);
		if (wsp) return wsp;
		wsp = this.wspsLive.findWsp(wspCd, true).listenChanges();
		this._wsps.set(wsp.code, wsp);
		this.setState(wsp.code, EWspWorkingSt.r);
		return wsp;
	}

	/** Enregistre un wsp existant. Attention, le même atelier ne peut-être référencé plusieurs fois. Préférer getWsp()*/
	registerWsp(wsp: Wsp): Wsp {
		const curr = this._wsps.get(wsp.code);
		if (curr === wsp) return wsp;
		if (curr) throw Error("Same wsp already registered");
		wsp.listenChanges();
		this._wsps.set(wsp.code, wsp);
		this.setState(wsp.code, EWspWorkingSt.r);
		return wsp;
	}

	/** Limite le dispatch d'events aux ateliers qui ont été réclamés via cette place par WspsLivePlace.getWsp(). */
	isConcernedByWsp(wspCd: wspCd): boolean {
		return this._wsps.has(wspCd);
	}

	/** Ce Wsp est-il exploité par cette place. */
	isWspUsed(wsp: Wsp): boolean {
		return this._wsps.get(wsp.code) === wsp;
	}

	/** Obtient un DocHolder d'une source pour ouvrir un éditeur. ATTENTION, doit être clos pour libérer la ressource. */
	async newDocHolder(wsp: Wsp, refUri: srcRef): Promise<IPlaceDocHolder | null> {
		const house = await this.wspsLive._getHouse(wsp, refUri);
		if (house) {
			//Import dynamique PlaceDocHolder.
			if (!PlaceDocHolderCstr) PlaceDocHolderCstr = (await import("lib/wsp/wspHouse.js")).PlaceDocHolder;
			return new PlaceDocHolderCstr(house, this);
		}
		return null;
	}

	/** Récupère une house pour un usage éphémère. */
	async getHouse(wsp: Wsp, refUri: srcRef): Promise<IWspXmlHouse | null> {
		return this.wspsLive._getHouse(wsp, refUri);
	}

	/** Notifie un changement d'état d'un wspUri par ce composant graphique. */
	setState(wspRef: wspRef, newSt: EWspWorkingSt): void {
		const oldSt = this._wspRefs.get(wspRef) || EWspWorkingSt.none;
		if (oldSt === newSt) return;
		if (newSt != EWspWorkingSt.none) {
			this._wspRefs.set(wspRef, newSt);
		} else {
			this._wspRefs.delete(wspRef);
		}
		const otherSt = this.wspsLive._getMergedServerStateFor(wspRef, this);
		if (WspsLive.getHigherState(otherSt, oldSt) !== WspsLive.getHigherState(otherSt, newSt) && this.wspsLive._connectionReady) {
			//Ce chgt de state provoque une modif de state général qui intéresse le serveur
			this.wspsLive.sendMsg({svc: 'wspWorking', type: 'state', uri: wspRef, state: newSt} as JWspWorkingMsgStateOut);
		}
	}

	// /** A n'utiliser que pour enregistrer des contenus non issus d'un DocHolder (binaires, imports externes...). */
	// async saveContent(wsp: Wsp, srcIdent: JSrcIdent, content: IBody, fields: ESrcField[]): Promise<JSrcFields> {
	// 	let qs = IO.qs("cdaction", "PutSrc", "param", wsp.code, "refUri", SRC.srcRef(srcIdent), "clId", wsp.wspServer.chain.clId, "fields", fields.join('*'));
	// 	let desc = await wsp.wspServer.config.wspSrcUrl.fetchJson<JSrcFields>(qs, {method: "PUT", body: content as any /*bug ts2.7 oubli URLSearchParams*/});
	// 	this.wspsLive._dispatchLocalChange(this, wsp, srcIdent, EWspChangesEvts.u, wsp.containsShortDescFields(fields) ? desc : null);
	// 	return desc;
	// }

	/** Retourne les states des autres devices. */
	getStates(wspRef: wspRef): Promise<JWspWorkingState[]> {
		return this.wspsLive._getStates(wspRef, true, this);
	}

	/** La house est-elle dirty au sein de ce client. */
	isHouseDirty(wspRef: wspRef): boolean {
		const house = this.wspsLive.getHouseIfFetched(wspRef);
		return house ? house.isDirty : false;
	}

	/** Abondonne une édition en cours et recharge l'item. */
	abortChangesAndReload(house: IWspXmlHouse): Promise<void> {
		return this.wspsLive._reloadHouse(house);
	}

	lockExternal(wspRef: wspRef): boolean {
		if (this.wspsLive._lockExternals(wspRef)) {
			if (this._lockExternals == null) this._lockExternals = new Set();
			this._lockExternals.add(wspRef);
			return true;
		}
		return false;
	}

	unlockExternal(wspRef: wspRef) {
		this._lockExternals.delete(wspRef);
		this.wspsLive._unlockExternals(wspRef);
	}

	closePlace(): void {
		for (const wsp of this._wsps.values()) wsp.stopListenChanges();
		for (const doc of this._docHolders) doc.close();
		// for (let [wspRef, oldSt] of this._wspRefs.entries()) {
		// 	let otherSt = this.wspsLive._getMergedServerStateFor(wspRef, this);
		// 	if (WspsLive.getHigherState(otherSt, oldSt) !== otherSt && this.wspsLive._connectionReady) {
		// 		//Ce chgt de state provoque une modif de state général qui intéresse le serveur
		// 		this.wspsLive.sendMsg({svc: 'wspWorking', type: 'state', uri: wspRef, state: otherSt});
		// 	}
		// }
		this.wspsLive._onPlaceClose(this);
		if (this._lockExternals) for (const wspRef of this._lockExternals) {
			this.wspsLive._unlockExternals(wspRef);
		}
	}

	_switchHouse(oldHouse: IWspXmlHouse, newHouse: IWspXmlHouse) {
		for (const docHolder of this._docHolders.values()) {
			if (docHolder.house === oldHouse) docHolder.switchHouse(newHouse);
		}
	}
}

//Lazy loading OT libs
let PlaceDocHolderCstr: typeof PlaceDocHolder;
let RoadExecFrameWsCstr: typeof RoadExecFrameWs;

/** */
export interface IWspsLivePlacePointer {
	place?: WspsLivePlace
}

export interface IPlaceDocHolder extends IDocHolderSync {
	house: IWspXmlHouse

	houseLstn: IEventsMgr<IWspXmlHouseEvents>

	readonly place: WspsLivePlace

	save(): Promise<void>
}

/**
 * Evènments dispatchés par une place.
 */
export interface IWspLivePlaceEvents extends IEvents {

	/**
	 * Chgt d'état (lecture/écriture) d'un noeud d'un atelier par un autre device.
	 * (note: les chgts d'état des autres places de ce même client ne sont pas dispatchés ici).
	 */
	newWspWorkingState: (wspRef: wspRef, wspCd: wspCd, srcRef: srcRef, st: EWspWorkingSt, account: string, clId: string) => void

	/** Etat dirty d'un item en édition. */
	houseDirtyChange: (wspRef: wspRef, wsp: Wsp, srcIdent: JSrcIdent, dirty: boolean) => void

	/**
	 * Evènement de modification d'un noeud d'un atelier.
	 * @param from
	 * 	- House en cas d'enregistrement de la house (auto ou explicite)
	 *  - ou 'server' si l'event est issu du serveur.
	 * 	- Place locale à l'origine de l'event
	 *  - ou 'local' si l'origine est extérieur (ex: création d'item)
	 */
	wspUriChange: (msg: JWspUriChangeMsg, from: WspsLivePlace | IWspXmlHouse | 'local' | 'server') => void

	/** Informe un changement d'état du cycle de vie du Wsp : utiliser wsp.isAvailable, wsp.isLoaded, wsp.isInError ... */
	wspLiveStateChange: (wsp: Wsp) => void

	/** Informe un changement d'état de chargement complet Wsp : utiliser wsp.isFullLoading, wsp.isFullLoaded */
	wspLiveFullLoadStateChange: (wsp: Wsp) => void

	/** Informe que la connection au serveur est interrompue. */
	onConnectionLost: (code: number, reason: string, wasClean: boolean) => void

	/** Nouveaux états des uris issus du server. Les états locaux ne sont pas remontés ici.*/
	onConnectionRenewed: (states: JWspWorkingInitStates) => void
}


/** Msg de réponse par le server à l'init de la connection (Server -> Client) */
type JWspWorkingInitIn = {
	svc: 'wspWorking'
	type: 'init'
	uris: JWspWorkingInitStates
}

/** Msg de dispatch aux clients de chgt de state d'une uri par un autre device (Server -> Client) */
export type JWspWorkingMsgStateIn = {
	svc: 'wspWorking'
	type: 'state'
	uri: wspRef
	state: EWspWorkingSt
	account: string
	clId: string
}

/** Réponse des states d'une uri (Server -> Client) */
type JWspWorkingStatesResp = {
	svc: 'wspWorking'
	type: 'states'
	reqId: any
	uri: wspUri
	srcDt?: number
	srcStamp?: string
	states?: JWspWorkingState[]
}

/** Msg d'envoi au server d'un chgt de state (Client -> Server) */
export type JWspWorkingMsgStateOut = {
	svc: 'wspWorking'
	type: 'state'
	uri: wspRef
	state: EWspWorkingSt
}